import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertTriangle, ArrowLeft, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPage = () => {
  const { nfcCode } = useParams();
  const navigate = useNavigate();
  const [resetting, setResetting] = useState(false);

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.delete(`${API}/tags/${nfcCode}/reset`);
      toast.success('Tag reset successfully');
      navigate(`/${nfcCode}`);
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset tag');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto bg-destructive/10 rounded-full flex items-center justify-center mb-6">
            <AlertTriangle className="w-12 h-12 text-destructive" strokeWidth={1.5} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold font-heading tracking-tight mb-4">
            Reset This Tag?
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            This will permanently delete all content, messages, and media associated with this NFC tag. This action cannot be undone.
          </p>
        </div>

        <div className="space-y-4">
          <button
            data-testid="confirm-reset-button"
            onClick={handleReset}
            disabled={resetting}
            className="w-full bg-destructive text-destructive-foreground rounded-full px-8 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          >
            <RotateCcw className="w-5 h-5" strokeWidth={1.5} />
            {resetting ? 'Resetting...' : 'Yes, Reset Tag'}
          </button>
          <button
            data-testid="cancel-reset-button"
            onClick={() => navigate(`/${nfcCode}`)}
            className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-full px-8 py-6 font-medium transition-all hover:scale-105 active:scale-95 shadow-sm hover:shadow-md flex items-center justify-center gap-3"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            Cancel
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ResetPage;